import { useEffect, useState } from "react";
import styled from "styled-components";
import confetti from "canvas-confetti";
import * as anchor from "@project-serum/anchor";
import {
  LAMPORTS_PER_SOL,
  PublicKey,
  Connection,
  clusterApiUrl,
} from "@solana/web3.js";
import { useAnchorWallet } from "@solana/wallet-adapter-react";
import { WalletMultiButton } from "@solana/wallet-adapter-react-ui";
import { GatewayProvider } from "@civic/solana-gateway-react";
import Countdown from "react-countdown";
import { Snackbar, Paper, LinearProgress, Chip } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { toDate, AlertState, getAtaForMint } from "./utils";
import { MintButton } from "./MintButton";
import { WalletAdapterNetwork } from "@solana/wallet-adapter-base";
import * as metaPlx from "@metaplex/js";
import { Metadata } from "@metaplex-foundation/mpl-token-metadata";
import {
  CandyMachine,
  awaitTransactionSignatureConfirmation,
  getCandyMachineState,
  mintOneToken,
  CANDY_MACHINE_PROGRAM,
} from "./candy-machine";

// import { Connection, clusterApiUrl, PublicKey } from '@solana/web3.js';
import bs58 from "bs58";
// import { ConnectionTest } from '@metaplex/js';
// import { Metadata } from '@metaplex-foundation/mpl-token-metadata';
import * as pbKey from "@solana/web3.js";
import {
  getParsedNftAccountsByOwner,
  isValidSolanaAddress,
  createConnectionConfig,
} from "@nfteyez/sol-rayz";
import { db } from "./resources/firebaseConfig";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";

const cluster = process.env.REACT_APP_SOLANA_NETWORK!.toString();
const decimals = process.env.REACT_APP_SPL_TOKEN_TO_MINT_DECIMALS
  ? +process.env.REACT_APP_SPL_TOKEN_TO_MINT_DECIMALS!.toString()
  : 9;
const splTokenName = process.env.REACT_APP_SPL_TOKEN_TO_MINT_NAME
  ? process.env.REACT_APP_SPL_TOKEN_TO_MINT_NAME.toString()
  : "TOKEN";

const WalletContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
`;

const WalletAmount = styled.div`
  color: black;
  width: auto;
  padding: 5px 5px 5px 16px;
  min-width: 48px;
  min-height: auto;
  border-radius: 22px;
  background-color: var(--main-text-color);
  box-shadow: 0px 3px 5px -1px rgb(0 0 0 / 20%),
    0px 6px 10px 0px rgb(0 0 0 / 14%), 0px 1px 18px 0px rgb(0 0 0 / 12%);
  box-sizing: border-box;
  transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  font-weight: 500;
  line-height: 1.75;
  text-transform: uppercase;
  border: 0;
  margin: 0;
  display: inline-flex;
  outline: 0;
  position: relative;
  align-items: center;
  user-select: none;
  vertical-align: middle;
  justify-content: flex-start;
  gap: 10px;
`;

const Wallet = styled.ul`
  flex: 0 0 auto;
  margin: 0;
  padding: 0;
`;

const ConnectButton = styled(WalletMultiButton)`
  border-radius: 18px !important;
  padding: 6px 16px;
  background-color: #4e44ce;
  margin: 0 auto;
`;

const NFT = styled(Paper)`
  min-width: 500px;
  padding: 5px 20px 20px 20px;
  flex: 1 1 auto;
  background-color: var(--card-background-color) !important;
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22) !important;
`;

const Des = styled(NFT)`
  text-align: left;
  padding-top: 0px;
`;

const Card = styled(Paper)`
  display: inline-block;
  background-color: var(card-background-lighter-color) !important;
  margin: 5px;
  min-width: 40px;
  padding: 24px;
  h1 {
    margin: 0px;
  }
`;

const MintButtonContainer = styled.div`
  button.MuiButton-contained:not(.MuiButton-containedPrimary).Mui-disabled {
    color: #464646;
  }

  button.MuiButton-contained:not(.MuiButton-containedPrimary):hover,
  button.MuiButton-contained:not(.MuiButton-containedPrimary):focus {
    -webkit-animation: pulse 1s;
    animation: pulse 1s;
    box-shadow: 0 0 0 2em rgba(255, 255, 255, 0);
  }

  @-webkit-keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 #ef8f6e;
    }
  }

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 #ef8f6e;
    }
  }
`;

const Logo = styled.div`
  flex: 0 0 auto;

  img {
    height: 60px;
  }
`;
const Menu = styled.ul`
  list-style: none;
  display: inline-flex;
  flex: 1 0 auto;

  li {
    margin: 0 12px;

    a {
      color: var(--main-text-color);
      list-style-image: none;
      list-style-position: outside;
      list-style-type: none;
      outline: none;
      text-decoration: none;
      text-size-adjust: 100%;
      touch-action: manipulation;
      transition: color 0.3s;
      padding-bottom: 15px;

      img {
        max-height: 26px;
      }
    }

    a:hover,
    a:active {
      color: rgb(131, 146, 161);
      border-bottom: 4px solid var(--title-text-color);
    }
  }
`;

const SolExplorerLink = styled.a`
  color: var(--title-text-color);
  border-bottom: 1px solid var(--title-text-color);
  font-weight: bold;
  list-style-image: none;
  list-style-position: outside;
  list-style-type: none;
  outline: none;
  text-decoration: none;
  text-size-adjust: 100%;

  :hover {
    border-bottom: 2px solid var(--title-text-color);
  }
`;

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
  margin-bottom: 20px;
  margin-right: 4%;
  margin-left: 4%;
  text-align: center;
  justify-content: center;
`;

const MintContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1 1 auto;
  flex-wrap: wrap;
  gap: 20px;
`;

const DesContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  gap: 20px;
`;

const Price = styled(Chip)`
  position: absolute;
  margin: 5px;
  font-weight: bold;
  font-size: 1.2em !important;
  font-family: "Patrick Hand", cursive !important;
`;

const Image = styled.img`
  height: 400px;
  width: auto;
  border-radius: 7px;
  box-shadow: 5px 5px 40px 5px rgba(0, 0, 0, 0.5);
`;

const BorderLinearProgress = styled(LinearProgress)`
  margin: 20px;
  height: 10px !important;
  border-radius: 30px;
  border: 2px solid white;
  box-shadow: 5px 5px 40px 5px rgba(0, 0, 0, 0.5);
  background-color: var(--main-text-color) !important;

  > div.MuiLinearProgress-barColorPrimary {
    background-color: var(--title-text-color) !important;
  }

  > div.MuiLinearProgress-bar1Determinate {
    border-radius: 30px !important;
    background-image: linear-gradient(
      270deg,
      rgba(255, 255, 255, 0.01),
      rgba(255, 255, 255, 0.5)
    );
  }
`;

const ShimmerTitle = styled.h1`
  margin: 20px auto;
  text-transform: uppercase;
  animation: glow 2s ease-in-out infinite alternate;
  color: var(--main-text-color);
  @keyframes glow {
    from {
      text-shadow: 0 0 20px var(--main-text-color);
    }
    to {
      text-shadow: 0 0 30px var(--title-text-color),
        0 0 10px var(--title-text-color);
    }
  }
`;

const GoldTitle = styled.h2`
  color: var(--title-text-color);
`;

const LogoAligner = styled.div`
  display: flex;
  align-items: center;

  img {
    max-height: 35px;
    margin-right: 10px;
  }
`;

export interface HomeProps {
  candyMachineId: anchor.web3.PublicKey;
  connection: anchor.web3.Connection;
  txTimeout: number;
  rpcHost: string;
  network: WalletAdapterNetwork;
}

const Home = (props: HomeProps) => {
  const [balance, setBalance] = useState<number>();
  const [isMinting, setIsMinting] = useState(false); // true when user got to press MINT
  const [isActive, setIsActive] = useState(false); // true when countdown completes or whitelisted
  const [solanaExplorerLink, setSolanaExplorerLink] = useState<string>("");
  const [itemsAvailable, setItemsAvailable] = useState(0);
  const [itemsRedeemed, setItemsRedeemed] = useState(0);
  const [itemsRemaining, setItemsRemaining] = useState(0);
  const [isSoldOut, setIsSoldOut] = useState(false);
  const [payWithSplToken, setPayWithSplToken] = useState(false);
  const [price, setPrice] = useState(0);
  const [priceLabel, setPriceLabel] = useState<string>("SOL");
  const [whitelistPrice, setWhitelistPrice] = useState(0);
  const [whitelistEnabled, setWhitelistEnabled] = useState(false);
  const [isBurnToken, setIsBurnToken] = useState(false);
  const [whitelistTokenBalance, setWhitelistTokenBalance] = useState(0);
  const [isEnded, setIsEnded] = useState(false);
  const [endDate, setEndDate] = useState<Date>();
  const [isPresale, setIsPresale] = useState(false);
  const [isWLOnly, setIsWLOnly] = useState(false);
  const [isBuyFirst, setIsBuyFirst] = useState(false);
  const [nixWallet, setNixWallet] = useState([]);
  const [isTotalMint, setIsTotalMint] = useState("");
  const [getTotalPass, setgetTotalPass] = useState(0);

  const [alertState, setAlertState] = useState<AlertState>({
    open: false,
    message: "",
    severity: undefined,
  });
  const walletNetwork = WalletAdapterNetwork.Mainnet;
  const wallet = useAnchorWallet();
  const [candyMachine, setCandyMachine] = useState<CandyMachine>();

  const rpcUrl = props.rpcHost;
  // const nixWalletColRef = collection(db, "nixWallet");

  const refreshCandyMachineState = () => {
    (async () => {
      if (!wallet) return;

      const cndy = await getCandyMachineState(
        wallet as anchor.Wallet,
        props.candyMachineId,
        props.connection
      );

      setCandyMachine(cndy);
      setItemsAvailable(cndy.state.itemsAvailable);
      setItemsRemaining(cndy.state.itemsRemaining);
      setItemsRedeemed(cndy.state.itemsRedeemed);

      var divider = 1;
      if (decimals) {
        divider = +("1" + new Array(decimals).join("0").slice() + "0");
      }

      const colRefget = doc(db, process.env.REACT_APP_FIREBASE_COLLECTION, wallet.publicKey.toBase58());
      const recordget = await getDoc(colRefget);

      // detect if using spl-token to mint
      if (cndy.state.tokenMint) {
        setPayWithSplToken(true);
        // Customize your SPL-TOKEN Label HERE
        // TODO: get spl-token metadata name
        setPriceLabel(splTokenName);
        setPrice(cndy.state.price.toNumber() / divider);
        setWhitelistPrice(cndy.state.price.toNumber() / divider);
      } else {
        setPrice(cndy.state.price.toNumber() / LAMPORTS_PER_SOL);
        setWhitelistPrice(cndy.state.price.toNumber() / LAMPORTS_PER_SOL);
      }

      // fetch whitelist token balance
      if (cndy.state.whitelistMintSettings) {
        setWhitelistEnabled(true);
        setIsBurnToken(cndy.state.whitelistMintSettings.mode.burnEveryTime);
        setIsPresale(cndy.state.whitelistMintSettings.presale);
        setIsWLOnly(
          !isPresale && cndy.state.whitelistMintSettings.discountPrice === null
        );

        if (
          cndy.state.whitelistMintSettings.discountPrice !== null &&
          cndy.state.whitelistMintSettings.discountPrice !== cndy.state.price
        ) {
          if (cndy.state.tokenMint) {
            setWhitelistPrice(
              cndy.state.whitelistMintSettings.discountPrice?.toNumber() /
                divider
            );
          } else {
            setWhitelistPrice(
              cndy.state.whitelistMintSettings.discountPrice?.toNumber() /
                LAMPORTS_PER_SOL
            );
          }
        }

        let balance = 0;
        try {
          const tokenBalance = await props.connection.getTokenAccountBalance(
            (
              await getAtaForMint(
                cndy.state.whitelistMintSettings.mint,
                wallet.publicKey
              )
            )[0]
          );

          balance = tokenBalance?.value?.uiAmount || 0;
        } catch (e) {
          console.error(e);
          balance = 0;
        }
        setWhitelistTokenBalance(balance);
        setIsActive(isPresale && !isEnded && balance > 0);
      } else {
        setWhitelistEnabled(false);
      }

      // end the mint when date is reached
      if (cndy?.state.endSettings?.endSettingType.date) {
        setEndDate(toDate(cndy.state.endSettings.number));
        if (
          cndy.state.endSettings.number.toNumber() <
          new Date().getTime() / 1000
        ) {
          setIsEnded(true);
          setIsActive(false);
        }
      }
      // end the mint when amount is reached
      if (cndy?.state.endSettings?.endSettingType.amount) {
        let limit = Math.min(
          cndy.state.endSettings.number.toNumber(),
          cndy.state.itemsAvailable
        );
        setItemsAvailable(limit);
        if (cndy.state.itemsRedeemed < limit) {
          setItemsRemaining(limit - cndy.state.itemsRedeemed);
        } else {
          setItemsRemaining(0);
          cndy.state.isSoldOut = true;
          setIsEnded(true);
        }
      } else {
        setItemsRemaining(cndy.state.itemsRemaining);
      }

      if (recordget.exists()) {
        setIsTotalMint(recordget.data()["totalMintStock"]);
      }

      if (cndy.state.isSoldOut) {
        setIsActive(false);
      }
    })();
  };

  const renderGoLiveDateCounter = ({ days, hours, minutes, seconds }: any) => {
    return (
      <div>
        <Card elevation={1}>
          <h1>{days}</h1>Days
        </Card>
        <Card elevation={1}>
          <h1>{hours}</h1>
          Hours
        </Card>
        <Card elevation={1}>
          <h1>{minutes}</h1>Mins
        </Card>
        <Card elevation={1}>
          <h1>{seconds}</h1>Secs
        </Card>
      </div>
    );
  };

  const renderEndDateCounter = ({ days, hours, minutes }: any) => {
    let label = "";
    if (days > 0) {
      label += days + " days ";
    }
    if (hours > 0) {
      label += hours + " hours ";
    }
    label += minutes + 1 + " minutes left to MINT.";
    return (
      <div>
        <h3>{label}</h3>
      </div>
    );
  };

  function displaySuccess(mintPublicKey: any): void {
    let remaining = itemsRemaining - 1;
    setItemsRemaining(remaining);
    setIsSoldOut(remaining === 0);
    if (isBurnToken && whitelistTokenBalance && whitelistTokenBalance > 0) {
      let balance = whitelistTokenBalance - 1;
      setWhitelistTokenBalance(balance);
      setIsActive(isPresale && !isEnded && balance > 0);
    }
    setItemsRedeemed(itemsRedeemed + 1);
    const solFeesEstimation = 0.012; // approx
    if (!payWithSplToken && balance && balance > 0) {
      setBalance(
        balance -
          (whitelistEnabled ? whitelistPrice : price) -
          solFeesEstimation
      );
    }
    setSolanaExplorerLink(
      cluster === "devnet" || cluster === "testnet"
        ? "https://solscan.io/token/" + mintPublicKey + "?cluster=" + cluster
        : "https://solscan.io/token/" + mintPublicKey
    );
    throwConfetti();
  }

  function throwConfetti(): void {
    confetti({
      particleCount: 400,
      spread: 70,
      origin: { y: 0.6 },
    });
  }

  const getMintAddress = async () => {
    let anchorConnection = new anchor.web3.Connection(
      process.env.REACT_APP_SOLANA_RPC_HOST!
    );

    const nfts = await getParsedNftAccountsByOwner({
      publicAddress: wallet.publicKey.toBase58(),
      connection: anchorConnection,
      sanitize: false,
    });

    return nfts;
  };

  const onMint = async () => {
    try {
      setIsMinting(true);

      let getMint = await getMintAddress();
      let totalMint = 0;
      let first = 0;
      let second = 0;
      let third = 0;
      let firstTotal = 0;
      let secondTotal = 0;
      let thirdTotal = 0;
      let total = 0;
      let totalAvailableMint = 0;
      let stockMint = 0;

      if (getMint[0] !== undefined) {
        getMint.forEach(async (data) => {
          if (data.data.name.includes("Abstract Labs Inner Circle")) {
            first = first += 1;
          } else if (data.data.name.includes("Plane-T")) {
            second = second += 1;
          } else if (data.data.name.includes("Abstract Labs Standard")) {
            third = third += 1;
          }
        });
      }

      firstTotal = first * 10;
      secondTotal = second * 3;
      thirdTotal = third * 1;
      total = first + second + third;
      totalAvailableMint = firstTotal + secondTotal + thirdTotal;
      console.log(total);
      console.log(totalMint);

      if (wallet && candyMachine?.program && wallet.publicKey) {
        if (total != 0) {
          // console.log("total :" + total);
          const mint = anchor.web3.Keypair.generate();
          const mintTxId = (
            await mintOneToken(candyMachine, wallet.publicKey, mint)
          )[0];

          let status: any = { err: true };
          if (mintTxId) {
            status = await awaitTransactionSignatureConfirmation(
              mintTxId,
              props.txTimeout,
              props.connection,
              "singleGossip",
              true
            );
          }

          if (!status?.err) {
            setAlertState({
              open: true,
              message: "Congratulations! Mint succeeded!",
              severity: "success",
            });
            console.log("mint success");

            // update front-end amounts
            displaySuccess(mint.publicKey);

            //get all document from database
            // const data = await getDocs(nixWalletColRef);
            // const spec = data.docs.map((doc) => ({
            //   ...doc.data(),
            //   id: doc.id,
            // }));
            // console.log(spec);

            var condition = false;
            console.log("wallet active : " + wallet.publicKey.toBase58());
            const colRefget = doc(db, process.env.REACT_APP_FIREBASE_COLLECTION, wallet.publicKey.toBase58());
            const recordget = await getDoc(colRefget);

            //Check if wallet address is available in database
            // if (spec !== undefined) {
            //   spec.forEach(async (data) => {
            if (recordget.exists()) {
              // console.log(
              //   "check wallet from login : " + wallet.publicKey.toBase58()
              // );
              // console.log(
              //   "check wallet from db : " + recordget.data()["walletAddress"]
              // );
              var check =
                recordget.data()["walletAddress"] ===
                wallet.publicKey.toBase58();
            }
            // var check =
            // data["walletAddress"] === wallet.publicKey.toBase58();
            if (check === true) {
              condition = true;
            }
            //   });
            // }

            console.log("final condition : " + condition);

            //get DocumentReference from collection database
            // const colRef = doc(db, process.env.REACT_APP_FIREBASE_COLLECTION, wallet.publicKey.toBase58());
            // const record = await getDoc(colRef);

            //add new mint address to database
            const addWalletToDb = async (
              walletAddress,
              firstPass,
              firstTotal,
              secondPass,
              secondTotal,
              thirdPass,
              thirdTotal,
              totalPass,
              totalMintStock,
              stockMint
            ) => {
              await setDoc(colRefget, {
                walletAddress: walletAddress,
                firstPass: firstPass,
                firstTotal: firstTotal,
                secondPass: secondPass,
                secondTotal: secondTotal,
                thirdPass: thirdPass,
                thirdTotal: thirdTotal,
                totalPass: totalPass,
                totalMintStock: totalMintStock,
                stockMint: stockMint,
              });
              console.log("create success");
            };

            //update totalMintStock if mint address was recorded in database
            const updateWalletToDb = async (
              recordTotal,
              recordStock,
              address
            ) => {
              const walletDoc = doc(db, process.env.REACT_APP_FIREBASE_COLLECTION, address);
              const newStock = {
                totalMintStock: recordTotal - 1,
                stockMint: recordStock + 1,
              };
              await updateDoc(walletDoc, newStock);
              console.log("update succeded");
            };

            //check if specific data is available
            if (condition == false) {
              addWalletToDb(
                wallet.publicKey.toBase58(),
                first,
                firstTotal,
                second,
                secondTotal,
                third,
                thirdTotal,
                total,
                totalAvailableMint,
                stockMint + 1
              );
              console.log("add wallet");
              console.log("minting success");
            } else {
              if (recordget.exists()) {
                // console.log("Stock data:", recordget.data()["totalMintStock"]);
                //get DocumentReference from collection database
                // const colRefget = doc(
                //   db,
                //   process.env.REACT_APP_FIREBASE_COLLECTION,
                //   wallet.publicKey.toBase58()
                // );
                // const recordget = await getDoc(colRefget);

                //refresh account
                let dbFirst = recordget.data()["firstPass"];
                let dbSecond = recordget.data()["secondPass"];
                let dbThird = recordget.data()["thirdPass"];

                // latest variable
                let latestFirst = 0;
                let latestTotalFirst = 0;
                let latestSecond = 0;
                let latestTotalSecond = 0;
                let latestThird = 0;
                let latestTotalThird = 0;
                let latestTotal = 0;
                let latestTotalPass = 0;
                let latestTotalMintStock = 0;

                //define and update latest pass
                if (first !== dbFirst) {
                  latestFirst = first;
                  latestTotalFirst = latestFirst * 10;
                  console.log(latestFirst);
                  // const walletDoc = doc(
                  //   db,
                  //   process.env.REACT_APP_FIREBASE_COLLECTION,
                  //   wallet.publicKey.toBase58()
                  // );
                  const newStock = {
                    firstPass: latestFirst,
                    firstTotal: latestTotalFirst,
                  };
                  await updateDoc(colRefget, newStock);
                  console.log("update single pass succeded");
                }
                if (second !== dbSecond) {
                  latestSecond = second;
                  latestTotalSecond = latestSecond * 3;
                  console.log(latestSecond);
                  // const walletDoc = doc(
                  //   db,
                  //   process.env.REACT_APP_FIREBASE_COLLECTION,
                  //   wallet.publicKey.toBase58()
                  // );
                  const newStock = {
                    secondPass: latestSecond,
                    secondTotal: latestTotalSecond,
                  };
                  await updateDoc(colRefget, newStock);
                  console.log("update single pass succeded");
                }
                if (third !== dbThird) {
                  latestThird = third;
                  latestTotalThird = latestThird * 1;
                  console.log(latestThird);
                  // const walletDoc = doc(
                  //   db,
                  //   process.env.REACT_APP_FIREBASE_COLLECTION,
                  //   wallet.publicKey.toBase58()
                  // );
                  const newStock = {
                    thirdPass: latestThird,
                    thirdTotal: latestTotalThird,
                  };
                  await updateDoc(colRefget, newStock);
                  console.log("update single pass succeded");
                }

                // define latest total mint stock
                if (latestTotalFirst === 0) {
                  latestTotalFirst = recordget.data()["firstTotal"];
                }
                if (latestTotalSecond === 0) {
                  latestTotalSecond = recordget.data()["secondTotal"];
                }
                if (latestTotalThird === 0) {
                  latestTotalThird = recordget.data()["thirdTotal"];
                }

                // define latest total pass
                if (latestFirst === 0) {
                  latestFirst = recordget.data()["firstPass"];
                }
                if (latestSecond === 0) {
                  latestSecond = recordget.data()["secondPass"];
                }
                if (latestThird === 0) {
                  latestThird = recordget.data()["thirdPass"];
                }

                //update latest total pass and total mint stock
                latestTotalPass = latestFirst + latestSecond + latestThird;
                latestTotal =
                  latestTotalFirst + latestTotalSecond + latestTotalThird;
                console.log(latestTotal);
                latestTotalMintStock =
                  latestTotal - recordget.data()["stockMint"];
                console.log(latestTotalMintStock);
                // const walletDoc = doc(
                //   db,
                //   process.env.REACT_APP_FIREBASE_COLLECTION,
                //   wallet.publicKey.toBase58()
                // );
                const newStock = {
                  totalPass: latestTotalPass,
                  totalMintStock: latestTotalMintStock,
                };
                await updateDoc(colRefget, newStock);
                console.log("all stock updated");
                if (
                  latestTotalMintStock !== recordget.data()["totalMintStock"]
                ) {
                  latestTotalMintStock = latestTotalMintStock;
                }
                updateWalletToDb(
                  latestTotalMintStock,
                  recordget.data()["stockMint"],
                  wallet.publicKey.toBase58()
                );
                console.log("update wallet");
                setIsTotalMint(recordget.data()["totalMintStock"]);
                console.log("minting success");
              } else {
                console.log("No such stock available!");
              }
            }
          } else {
            setAlertState({
              open: true,
              message: "Mint failed! Please try again!",
              severity: "error",
            });
          }
        } else {
          setAlertState({
            open: true,
            message: "You don't have any Pass, let's start buy a Pass first!",
            severity: "error",
          });
        }
      }
    } catch (error: any) {
      // TODO: blech:
      let message = error.msg || "Minting failed! Please try again!";
      if (!error.msg) {
        if (!error.message) {
          message = "Transaction Timeout! Please try again.";
        } else if (error.message.indexOf("0x138")) {
        } else if (error.message.indexOf("0x137")) {
          message = `SOLD OUT!`;
        } else if (error.message.indexOf("0x135")) {
          message = `Insufficient funds to mint. Please fund your wallet.`;
        }
      } else {
        if (error.code === 311) {
          message = `SOLD OUT!`;
        } else if (error.code === 312) {
          message = `Minting period hasn't started yet.`;
        }
      }

      setAlertState({
        open: true,
        message,
        severity: "error",
      });
    } finally {
      setIsMinting(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (walletNetwork) {
        if (wallet) {
          let getMint = await getMintAddress();
          let firstC = 0;
          let secondC = 0;
          let thirdC = 0;
          let totalC = 0;

          if (getMint[0] !== undefined) {
            getMint.forEach(async (data) => {
              if (data.data.name.includes("Abstract Labs Inner Circle")) {
                firstC = firstC += 1;
              } else if (data.data.name.includes("Plane-T")) {
                secondC = secondC += 1;
              } else if (data.data.name.includes("Abstract Labs Standard")) {
                thirdC = thirdC += 1;
              }
            });
          }

          totalC = firstC + secondC + thirdC;

          if (totalC == 0) {
            setIsBuyFirst(true);
          }

          const balance = await props.connection.getBalance(wallet.publicKey);
          setBalance(balance / LAMPORTS_PER_SOL);
          // checkPass(total);
          const colRefget = doc(db, process.env.REACT_APP_FIREBASE_COLLECTION, wallet.publicKey.toBase58());
          const recordget = await getDoc(colRefget);
          if (recordget.exists()) {
            setIsTotalMint(recordget.data()["totalMintStock"]);

            // const data = await getDocs(nixWalletColRef);
            // const spec = data.docs.map((doc) => ({
            //   ...doc.data(),
            //   id: doc.id,
            // }));
            // console.log(spec);
            var condition = false;
            console.log("wallet active : " + wallet.publicKey.toBase58());

            //Check if wallet address is available in database
            // if (spec !== undefined) {
            //   spec.forEach(async (data) => {
            var check =
              recordget.data()["walletAddress"] === wallet.publicKey.toBase58();
            // var check = data["walletAddress"] === wallet.publicKey.toBase58();
            if (check === true) {
              condition = true;
            }
            // });
            // }

            console.log("final condition : " + condition);
            let getMint = await getMintAddress();
            let first = 0;
            let second = 0;
            let third = 0;
            let firstTotal = 0;
            let secondTotal = 0;
            let thirdTotal = 0;
            let total = 0;
            let totalAvailableMint = 0;
            let stockMint = 0;

            if (getMint[0] !== undefined) {
              getMint.forEach(async (data) => {
                if (data.data.name.includes("Abstract Labs Inner Circle")) {
                  first = first += 1;
                } else if (data.data.name.includes("Plane-T")) {
                  second = second += 1;
                } else if (data.data.name.includes("Abstract Labs Standard")) {
                  third = third += 1;
                }
              });
            }

            firstTotal = first * 10;
            secondTotal = second * 3;
            thirdTotal = third * 1;
            total = first + second + third;
            totalAvailableMint = firstTotal + secondTotal + thirdTotal;
            // const colRef = doc(db, process.env.REACT_APP_FIREBASE_COLLECTION, wallet.publicKey.toBase58());
            const equalMintStock = total == totalAvailableMint;
            if (condition !== false) {
              if (equalMintStock !== false) {
                // console.log(totalAvailableMint);
                // console.log(equalMintStock);
                const addWalletToDb = async (
                  walletAddress,
                  firstPass,
                  firstTotal,
                  secondPass,
                  secondTotal,
                  thirdPass,
                  thirdTotal,
                  totalPass,
                  totalMintStock,
                  stockMint
                ) => {
                  await setDoc(colRefget, {
                    walletAddress: walletAddress,
                    firstPass: firstPass,
                    firstTotal: firstTotal,
                    secondPass: secondPass,
                    secondTotal: secondTotal,
                    thirdPass: thirdPass,
                    thirdTotal: thirdTotal,
                    totalPass: totalPass,
                    totalMintStock: totalMintStock,
                    stockMint: stockMint,
                  });
                };
                addWalletToDb(
                  wallet.publicKey.toBase58(),
                  first,
                  firstTotal,
                  second,
                  secondTotal,
                  third,
                  thirdTotal,
                  total,
                  totalAvailableMint,
                  stockMint
                );
                console.log("set success");
                setIsTotalMint(totalAvailableMint.toString());
              } else {
                console.log("nothing changes");
              }
            }
          } else if (totalC !== 0) {
            setIsTotalMint(getTotalPass.toString());
            // console.log("wallet active : " + wallet.publicKey.toBase58());

            let getMint = await getMintAddress();
            let first = 0;
            let second = 0;
            let third = 0;
            let firstTotal = 0;
            let secondTotal = 0;
            let thirdTotal = 0;
            let total = 0;
            let totalAvailableMint = 0;
            let stockMint = 0;

            if (getMint[0] !== undefined) {
              getMint.forEach(async (data) => {
                if (data.data.name.includes("Abstract Labs Inner Circle")) {
                  first = first += 1;
                } else if (data.data.name.includes("Plane-T")) {
                  second = second += 1;
                } else if (data.data.name.includes("Abstract Labs Standard")) {
                  third = third += 1;
                }
              });
            }

            firstTotal = first * 10;
            secondTotal = second * 3;
            thirdTotal = third * 1;
            total = first + second + third;
            totalAvailableMint = firstTotal + secondTotal + thirdTotal;
            // const colRef = doc(db, process.env.REACT_APP_FIREBASE_COLLECTION, wallet.publicKey.toBase58());

            const addWalletToDb = async (
              walletAddress,
              firstPass,
              firstTotal,
              secondPass,
              secondTotal,
              thirdPass,
              thirdTotal,
              totalPass,
              totalMintStock,
              stockMint
            ) => {
              await setDoc(colRefget, {
                walletAddress: walletAddress,
                firstPass: firstPass,
                firstTotal: firstTotal,
                secondPass: secondPass,
                secondTotal: secondTotal,
                thirdPass: thirdPass,
                thirdTotal: thirdTotal,
                totalPass: totalPass,
                totalMintStock: totalMintStock,
                stockMint: stockMint,
              });
              console.log("create new success");
            };
            addWalletToDb(
              wallet.publicKey.toBase58(),
              first,
              firstTotal,
              second,
              secondTotal,
              third,
              thirdTotal,
              total,
              totalAvailableMint,
              stockMint
            );
          }
        }
      }
    })();
  }, [wallet, props.connection, setIsTotalMint]);

  useEffect(refreshCandyMachineState, [
    wallet,
    props.candyMachineId,
    props.connection,
    isEnded,
    isPresale,
  ]);

  return (
    <main>
      {props.network == process.env.REACT_APP_SOLANA_NETWORK as WalletAdapterNetwork ? (
        <>
          <MainContainer>
            <WalletContainer>
              <Wallet>
                {wallet ? (
                  <WalletAmount>
                    {(balance || 0).toLocaleString()} SOL
                    <ConnectButton />
                  </WalletAmount>
                ) : (
                  <br />
                )}
              </Wallet>
            </WalletContainer>
            <br />
            <MintContainer>
              <DesContainer>
                <NFT elevation={3}>
                  <MintButtonContainer>
                    <br />
                    {!isActive &&
                    !isEnded &&
                    candyMachine?.state.goLiveDate &&
                    (!isWLOnly || whitelistTokenBalance > 0) ? (
                      <Countdown
                        date={toDate(candyMachine?.state.goLiveDate)}
                        onMount={({ completed }) =>
                          completed && setIsActive(!isEnded)
                        }
                        onComplete={() => {
                          setIsActive(!isEnded);
                        }}
                        renderer={renderGoLiveDateCounter}
                      />
                    ) : isBuyFirst ? (
                      <>
                        <h1>Lets Buy Pass First!</h1>
                        <div className="btn-me">
                          <a href="https://magiceden.io/creators/abstract_labs">
                            <img src="assets/me-logo.png" alt="" width={150} />
                          </a>
                          <p>Buy on Magic Eden</p>
                        </div>
                      </>
                    ) : !wallet ? (
                      <ConnectButton>Connect Wallet</ConnectButton>
                    ) : !isWLOnly || whitelistTokenBalance > 0 ? (
                      candyMachine?.state.gatekeeper &&
                      wallet.publicKey &&
                      wallet.signTransaction ? (
                        <GatewayProvider
                          wallet={{
                            publicKey:
                              wallet.publicKey ||
                              new PublicKey(CANDY_MACHINE_PROGRAM),
                            //@ts-ignore
                            signTransaction: wallet.signTransaction,
                          }}
                          // // Replace with following when added
                          // gatekeeperNetwork={candyMachine.state.gatekeeper_network}
                          gatekeeperNetwork={
                            candyMachine?.state?.gatekeeper?.gatekeeperNetwork
                          } // This is the ignite (captcha) network
                          /// Don't need this for mainnet
                          clusterUrl={rpcUrl}
                          options={{ autoShowModal: false }}
                        >
                          <MintButton
                            candyMachine={candyMachine}
                            isMinting={isMinting}
                            isActive={isActive}
                            isEnded={isEnded}
                            isSoldOut={isSoldOut}
                            onMint={onMint}
                            wallet={wallet.publicKey.toBase58()}
                          />
                        </GatewayProvider>
                      ) : (
                        <>
                          <p>Total Mint Stock : {isTotalMint}</p>
                          <MintButton
                            candyMachine={candyMachine}
                            isMinting={isMinting}
                            isActive={isActive}
                            isEnded={isEnded}
                            isSoldOut={isSoldOut}
                            onMint={onMint}
                            wallet={wallet.publicKey.toBase58()}
                          />
                        </>
                      )
                    ) : (
                      <h1></h1>
                    )}
                  </MintButtonContainer>
                  <br />
                  {wallet && isActive && solanaExplorerLink && (
                    <SolExplorerLink href={solanaExplorerLink} target="_blank">
                      View on Solscan
                    </SolExplorerLink>
                  )}
                </NFT>
              </DesContainer>
            </MintContainer>
          </MainContainer>
        </>
      ) : (
        <h1>This page only support on Mainnet line</h1>
      )}
      <Snackbar
        open={alertState.open}
        autoHideDuration={6000}
        onClose={() => setAlertState({ ...alertState, open: false })}
      >
        <Alert
          onClose={() => setAlertState({ ...alertState, open: false })}
          severity={alertState.severity}
        >
          {alertState.message}
        </Alert>
      </Snackbar>
    </main>
  );
};

export default Home;
